if (!customElements.get('primo-floating-search')) {
  customElements.define('primo-floating-search', class extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.toggle = this.querySelector('.primo-floating-search__toggle');
      this.input = this.querySelector('input[type="search"]');
      this.mobile = window.matchMedia('(max-width: 989px)');
      this.toggle.addEventListener('click', () => {
        const expanded = !this.classList.contains('is-expanded');
        this.setExpanded(expanded);
        if (expanded && !this.isProductMobile()) this.input.focus();
      }, options);
      this.input.addEventListener('input', () => this.syncQuery(), options);
      this.querySelectorAll('[data-search-suggestion]').forEach(button => {
        button.addEventListener('click', () => {
          this.input.value = button.dataset.searchSuggestion;
          this.input.dispatchEvent(new Event('input', { bubbles: true }));
          this.input.focus();
        }, options);
      });
      this.addEventListener('click', event => {
        if (event.target === this && this.isProductMobile()) this.setExpanded(false);
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.mobile.matches && this.classList.contains('is-expanded')) {
          event.preventDefault();
          this.setExpanded(false);
          this.toggle.focus();
        }
        this.onModalKeydown(event);
      }, options);
      window.visualViewport?.addEventListener('resize', () => this.updateViewport(), options);
      window.visualViewport?.addEventListener('scroll', () => this.updateViewport(), options);
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) this.setExpanded(false);
      }, options);
      this.mobile.addEventListener('change', () => {
        // Preserve focus if the search is being used while the viewport changes.
        this.setExpanded(this.mobile.matches && this.querySelector('.primo-floating-search__panel').contains(document.activeElement));
        this.bindSource();
      }, options);
      this.scrolling = false;
      window.addEventListener('scroll', () => {
        this.scrolling = true;
        clearTimeout(this.scrollTimer);
        this.scheduleUpdate();
        this.scrollTimer = setTimeout(() => {
          this.scrolling = false;
          this.updateVisibility();
        }, 250);
      }, { ...options, passive: true });
      this.bindSource();
      document.addEventListener('shopify:section:load', () => this.bindSource(), options);
      document.addEventListener('shopify:section:unload', () => this.scheduleUpdate(), options);
      document.addEventListener('focusout', () => this.scheduleUpdate(), options);
      this.addEventListener('focusout', () => this.scheduleUpdate(), options);
    }

    setExpanded(expanded) {
      const wasModalOpen = this.modalOpen;
      this.classList.toggle('is-expanded', expanded);
      this.toggle.setAttribute('aria-expanded', String(expanded));
      this.toggle.setAttribute('aria-label', expanded ? this.toggle.dataset.closeLabel : this.toggle.dataset.searchLabel);
      const modalOpen = expanded && this.isProductMobile();
      this.modalOpen = modalOpen;
      document.body.classList.toggle('primo-search-open', modalOpen);
      if (modalOpen) {
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-label', 'Search products');
        if (!wasModalOpen) {
          this.setBackgroundInert(true);
          this.updateViewport();
          this.input.focus({ preventScroll: true });
        }
      } else {
        this.removeAttribute('role');
        this.removeAttribute('aria-modal');
        this.removeAttribute('aria-label');
        if (wasModalOpen) {
          this.setBackgroundInert(false);
          this.style.removeProperty('--primo-search-height');
          this.style.removeProperty('--primo-search-top');
          if (this.isConnected) this.toggle.focus({ preventScroll: true });
        }
      }
      this.syncQuery();
    }

    setBackgroundInert(inert) {
      if (!inert) {
        this.backgroundElements?.forEach(([element, previous]) => { element.inert = previous; });
        this.backgroundElements = null;
        return;
      }
      this.backgroundElements = [];
      // Isolate siblings at every level without making the dialog's ancestors inert.
      for (let current = this; current.parentElement; current = current.parentElement) {
        [...current.parentElement.children].filter(element => element !== current).forEach(element => {
          this.backgroundElements.push([element, element.inert]);
          element.inert = true;
        });
        if (current.parentElement === document.body) break;
      }
    }

    onModalKeydown(event) {
      if (!this.modalOpen || event.key !== 'Tab') return;
      const controls = [...this.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
        .filter(element => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus({ preventScroll: true });
      }
    }

    updateViewport() {
      if (!this.modalOpen || !window.visualViewport) return;
      this.style.setProperty('--primo-search-height', `${window.visualViewport.height}px`);
      this.style.setProperty('--primo-search-top', `${window.visualViewport.offsetTop}px`);
    }

    isProductMobile() {
      return this.dataset.source === 'header' && this.mobile.matches;
    }

    syncQuery() {
      this.classList.toggle('has-query', Boolean(this.input.value.trim()));
    }

    bindSource() {
      this.observer?.disconnect();
      this.source = document.querySelector(this.dataset.source === 'header' ? '.primo-nav__search .header-inline-search' : '.primo-hero .header-inline-search');
      if (this.source && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(() => this.updateVisibility(), { threshold: 0 });
        this.observer.observe(this.source);
      }
      this.updateVisibility();
    }

    scheduleUpdate() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateVisibility());
    }

    updateVisibility() {
      const productMobile = this.dataset.source === 'header' && this.mobile.matches;
      const homeMobile = this.dataset.source === 'hero' && this.mobile.matches;
      const sourceBounds = this.source?.isConnected && this.source.getBoundingClientRect();
      const pastSearch = sourceBounds && (homeMobile ? sourceBounds.top <= 12 : sourceBounds.bottom <= 0);
      // Do not interrupt an active query or suggestion selection with scroll dismissal.
      const searching = this.querySelector('.primo-floating-search__panel').contains(document.activeElement);
      const footer = document.querySelector('footer.pd-footer, footer.footer');
      const footerInView = footer && footer.getBoundingClientRect().top < window.innerHeight;
      const visible = homeMobile
        ? Boolean(searching || pastSearch)
        : productMobile
          ? Boolean(this.modalOpen || searching || !footerInView)
          : Boolean(!footerInView && (searching || (pastSearch && !this.scrolling)));
      this.classList.toggle('is-scrolling', !this.mobile.matches && this.scrolling && !searching);
      if (!visible) this.setExpanded(false);
      this.classList.toggle('is-visible', visible);
      this.inert = !visible;
      this.setAttribute('aria-hidden', String(!visible));
    }

    disconnectedCallback() {
      this.setBackgroundInert(false);
      document.body.classList.remove('primo-search-open');
      clearTimeout(this.scrollTimer);
      this.events?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  });
}
